// @flow

import { useMemo } from 'react';
import useComplianceApprovalCertsInStageQuery from '../../hooks/data-fetching/useComplianceApprovalCertsInStageQuery';
import type { CertsInStageViewModel } from './types';

// Step 10 — the drill-down panel now reads the live certs-in-a-stage endpoint
// for the currently selected stage and property, replacing step 9's static
// fixture. The card view-model shape is unchanged, so CertCard and the panel
// layout did not change; only the data source moved.
//
// Pagination is handled here: the query is an infinite query, so `certs` is
// every page fetched so far flattened into one list, and `loadMore` appends the
// next page onto the same cache entry without reloading the screen.
//
// Two contract details settled earlier and honoured here:
//   - certType is a display label resolved backend-side and is rendered exactly
//     as received; it is never derived or mapped in the frontend. ("Recert",
//     not "Recertification".)
//   - dueDate and isLate are always null in v1, so they are not read here and
//     not rendered. The card is built to absorb them later without a redesign.
export const useCertsInStage = ({
  organizationId,
  propertyId,
  stageKey,
}: {
  organizationId: ?string,
  propertyId: ?string,
  stageKey: ?string,
}): CertsInStageViewModel => {
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useComplianceApprovalCertsInStageQuery({
    organizationId,
    propertyId,
    stageKey,
  });

  // react-query is untyped under Flow in this repo, so `data` is loosely typed;
  // the response pages are read as a plain Object shape here — the fields are
  // the frozen contract (certs, totalCount, page, hasMore).
  const pages: Array<Object> = useMemo(
    () =>
      data && Array.isArray(data.pages)
        ? data.pages.map((page) => (page: Object))
        : [],
    [data],
  );

  // The stage's true total comes from the first page's header, not the number
  // of rows fetched so far — so the panel count reflects the whole stage even
  // before every page is loaded.
  const totalCount: number =
    typeof pages[0]?.totalCount === 'number' ? pages[0].totalCount : 0;

  const certs = useMemo(
    () =>
      pages
        .flatMap((page: Object) => page.certs ?? [])
        .map((cert: Object) => ({
          certId: cert.certId,
          householdName: cert.householdName,
          // A full, ready-to-use route built backend-side: for a resident
          // household it is the Resident Profile, for an applicant household the
          // Application Profile. The frontend links to it verbatim and never
          // rewrites the path — rewriting it here is what forced every link to
          // the wrong (household) route regardless of the cert's kind.
          householdLinkUrl: cert.householdLinkUrl,
          unit: cert.unit,
          program: cert.program,
          certType: cert.certType,
          effectiveDate: cert.effectiveDate,
          daysInStatus: cert.daysInStatus,
          statusChangedBy: cert.statusChangedBy,
          correctionRounds: cert.correctionRounds,
          previousStatusLabel: cert.previousStatusLabel,
        })),
    [pages],
  );

  return {
    certs,
    totalCount,
    isLoading,
    isError,
    // A valid response with no certs is a real, explicit empty state (an empty
    // stage), not an error and not "still loading".
    isEmpty: !isLoading && !isError && certs.length === 0,
    hasMore: Boolean(hasNextPage),
    isLoadingMore: isFetchingNextPage,
    loadMore: fetchNextPage,
  };
};
